import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  REPORT_PRESET_GROUPS,
  REPORT_PRESETS,
} from "@/common/report-configuration";
import type { ParitaWorkspace } from "@/common/user";
import { PARITA_COMMUNITY_DOMAIN } from "@/constants/user";
import { disabledReportPresetsFromDatasetConfig } from "@/lib/utils";
import {
  disabledPresetsForCommunity,
  quickStartPresetGroupsForCommunity,
} from "@/utils/communityQuickStartReportPresets";

interface QuickStartsProps {
  workspace: ParitaWorkspace;
  onSelectPreset: (presetKey: string, presetLabel: string) => void;
}

export function QuickStarts({ workspace, onSelectPreset }: QuickStartsProps) {
  const workspaceDatasetConfig = workspace.dataset_field_configs;
  let disabledPresets = disabledReportPresetsFromDatasetConfig(
    REPORT_PRESET_GROUPS,
    workspaceDatasetConfig,
  );

  let reportPresetGroups = REPORT_PRESET_GROUPS,
    reportPresets = Object.fromEntries(
      Object.entries(REPORT_PRESETS).map(([key, value]) => [key, value.label]),
    );

  if (workspace.domo_instance_domain === PARITA_COMMUNITY_DOMAIN) {
    reportPresetGroups = quickStartPresetGroupsForCommunity;
    disabledPresets = disabledPresetsForCommunity;
    reportPresets = {
      Performance: "Performance",
      Skills: "Skills",
      Training: "Training",
      "Internal Mobility": "Internal Mobility",
      Productivity: "Productivity",
      "HR Program ROI": "HR Program ROI",
      "Engagement Scores": "Engagement Scores",
      "Burnout Risk": "Burnout Risk",
      "Sentiment Trends": "Sentiment Trends",
      "Manager Effectiveness": "Manager Effectiveness",
      ...reportPresets,
    };
  }

  return (
    <Card className="w-full">
      <CardContent className="p-6 my-[50px]">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold mb-2">Quick Starts</h3>
          <p className="text-gray-500">
            Choose one to begin building your report, or select a category on
            the right.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {Object.entries(reportPresetGroups).map(([groupLabel, presets]) => (
            <fieldset
              key={groupLabel}
              className="border border-gray-300 dark:border-gray-600 rounded-lg p-4"
            >
              <legend className="px-2 text-lg leading-tight text-center font-semibold text-gray-700 dark:text-gray-300">
                {groupLabel}
              </legend>
              <div className="grid grid-cols-1 gap-3">
                {presets.map((key) => {
                  const presetLabel = reportPresets[key];
                  let disabled = disabledPresets.includes(key);
                  if (!presetLabel) disabled = true;
                  return (
                    <Button
                      key={key}
                      variant="ghost"
                      disabled={disabled}
                      className="h-auto text-md py-0 px-4 text-center hover:bg-primary hover:text-primary-foreground transition-colors"
                      onClick={() => {
                        if (presetLabel) {
                          onSelectPreset(key, presetLabel);
                        }
                      }}
                    >
                      {presetLabel}
                    </Button>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
